


















































































import Vue from "vue";
import { Location } from "vue-router";
import {
  ROUTES,
  EXERCISE_TYPE,
  VUEX_ASSIGNMENT_ACTION_NAMES,
  VUEX_ASSIGNMENT_NAMESPACE,
  VUEX_ROOT_ACTION_NAMES,
  VUEX_ROOT_GETTER_NAMES
} from "@tt-app/constants";
import {
  ApiExerciseBtnAction,
  ChatState,
  ChatAllObjects,
  ConversationProblemsState,
  ExerciseFinalScore,
  ExerciseViewData,
  ExerciseViewMethods,
  MicrophoneState
} from "@tt-app/models/exercise/";
import {
  chatHandler,
  chatProblemsHandler,
  exerciseHandler,
  exerciseNavHandler,
  getNewExerciseViewState,
  MainLogger,
  microphoneHandler,
  isNonTrophyAction,
  FlatExerciseListDetails,
  nextExerciseRouter
} from "@tt-app/utils";
import { MicData } from "@joyz/app-core/models/microphone";
import { MicConfig, MicImplName } from "@joyz/app-core/utils/microphone";
import { micPermissionInspector } from "@tt-app/utils/audio/micPermissionInspector";
import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";

import AssignmentCompletionModal from "@tt-app/components/assignment/AssignmentCompletionModal.vue";
import ChatBody from "@tt-app/components/exercise/ChatBody.vue";
import ChatTrophy from "@tt-app/components/exercise/ChatTrophy.vue";
import ExerciseHeaderTitle from "@tt-app/components/exercise/ExerciseHeaderTitle.vue";
import LayoutExercise from "@tt-app/layouts/LayoutExercise.vue";
import SttConnectionMethodModal from "@tt-app/components/exercise/SttConnectionMethodModal.vue";
import TtMic from "@tt-app/components/exercise/TtMic.vue";

import IconDict from "@joyz/app-core/assets/icons/default/Dictionary.svg";
import {
  LimitedModeExercise,
  Session,
  TerratalkLiveGetterNames,
  TerratalkLiveNamespace
} from "@tt-app/store/terratalkLive/types";
import { TERRATALK_LIVE_ROUTES } from "@tt-app/constants/router/terratalkLive";
import { audioContext } from "@tt-app/utils/audio/audioContext";

import { connectionCheck } from "@tt-app/utils/logger/connectionCheck";

const { DETAILS_REQUEST } = VUEX_ASSIGNMENT_ACTION_NAMES;
const { FETCH_DETAILS } = VUEX_ROOT_ACTION_NAMES;
const { FLAT_EXERCISE_LIST } = VUEX_ROOT_GETTER_NAMES;

interface Props {
  iconDict: string;
}

interface Data extends ExerciseViewData {
  audioDecoderPlayer: AudioDecoderPlayer;
  chat: ChatState;
  chatProblems: ConversationProblemsState;
  mic: MicrophoneState;
  micPermission: boolean;
  modalOpened: boolean;
  silenceDetected: boolean;
}

interface Methods extends ExerciseViewMethods {
  closeConnectionMethodModal: () => void;
  fetchAssignmentDetails: () => void;
  fetchDetails: () => void;
  initChat: () => Promise<void>;
  loadProblemsQueue: () => void;
  showNextChatMessage: () => Promise<void>;
  onActionBtnClick: (action: ApiExerciseBtnAction) => void;
  onConnectionError: (error: Error) => void;
  onDictionaryClick: () => void;
  onMicSend: (data: MicData[]) => void;
  onMicTimeout: () => void;
  onMicStarted: () => void;
  onMicStopped: () => Promise<void>;
  onSilenceDetected: () => void;
  refreshAudioPlayer: () => void;
  updateFooter: () => void;
}

interface Computed {
  assignmentId?: string;
  assignmentsCompleted: number;
  courseUuid: string;
  currentExerciseListIndex: number | null;
  exerciseUuid: string;
  feedbackRouteParams: {
    withFeedback: string;
    logId: string;
  };
  flatExerciseList: FlatExerciseListDetails[];
  limitedModeExercises: LimitedModeExercise[];
  micConfig: MicConfig;
  micImplementation: MicImplName;
  nextExercise?: FlatExerciseListDetails;
  nextExerciseButtonText: string;
  session: Session;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    AssignmentCompletionModal,
    SttConnectionMethodModal,
    ExerciseHeaderTitle,
    ChatBody,
    ChatTrophy,
    LayoutExercise,
    TtMic
  },

  props: {
    iconDict: { type: String, default: IconDict }
  },

  data() {
    return {
      ...getNewExerciseViewState(EXERCISE_TYPE.CHAT, {
        name: ROUTES.COURSE.DETAILS.NAME,
        params: { courseUuid: this.$route.params.courseUuid }
      }),
      audioDecoderPlayer: new AudioDecoderPlayer({ audioContext }),
      chat: chatHandler.getNewState(),
      chatProblems: chatProblemsHandler.getNewState(),
      mic: microphoneHandler.getNewState(),
      micPermission: false,
      modalOpened: false,
      silenceDetected: false
    };
  },

  computed: {
    assignmentId() {
      return this.$route.params.assignmentId;
    },
    assignmentsCompleted() {
      const completedCount = this.exercise?.results?.trophyInfo?.numAssignmentsSubmitted;
      return completedCount || 0;
    },
    courseUuid() {
      return this.$route.params.courseUuid;
    },
    currentExerciseListIndex() {
      const listIndex = this.flatExerciseList.findIndex(
        (exerciseDetails: FlatExerciseListDetails) => {
          return exerciseDetails.uuid === this.exerciseUuid;
        }
      );

      return listIndex >= 0 ? listIndex : null;
    },
    exerciseUuid() {
      return this.$route.params.exerciseUuid;
    },
    feedbackRouteParams() {
      const exerciseStarted = this.exercise.logId > 0;
      const exerciseFinished = this.exercise.status === "finished";

      return {
        withFeedback: Boolean(exerciseStarted && !exerciseFinished).toString(),
        logId: this.exercise.logId.toString()
      };
    },
    flatExerciseList() {
      return this.$store.getters[FLAT_EXERCISE_LIST]({
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },
    limitedModeExercises() {
      return this.$store.getters[
        `${TerratalkLiveNamespace}/${TerratalkLiveGetterNames.LIMITED_MODE_EXERCISES}`
      ];
    },
    micImplementation() {
      return this.$store.getters["user/micImplementation"];
    },
    micConfig() {
      return {
        impl: this.micImplementation,
        options: {
          audioCtx: audioContext,
          onRecordingStarted: () => this.onMicStarted(),
          onRecognizingStopped: () => this.onMicStopped(),
          onSilenceDetected: () => this.onSilenceDetected(),
          queueIntervalPeriod: 1000
        }
      };
    },
    nextExercise() {
      if (this.currentExerciseListIndex === null) return undefined;
      return this.flatExerciseList[this.currentExerciseListIndex + 1];
    },
    nextExerciseButtonText() {
      let i18nString = "exercise.end";

      if (this.nextExercise) {
        i18nString = "exercise.next";
      }

      return this.$__(i18nString);
    },
    session() {
      return this.$store.getters[
        `${TerratalkLiveNamespace}/${TerratalkLiveGetterNames.ACTIVE_SESSION}`
      ];
    }
  },

  watch: {
    "nav.requireExitConfirmation": {
      handler(newValue) {
        window.onbeforeunload = null;

        if (newValue) {
          window.onbeforeunload = () => {
            return true;
          };
        }
      }
    },
    "chat.display.lastItem": {
      async handler(lastItem: ChatAllObjects, oldItem: ChatAllObjects) {
        // the chat object can be modified for different reason so the
        // lastItem watcher can be triggered multiple times while it
        // hasn't changed
        if (oldItem === lastItem) {
          return;
        }

        // Ensure that footer buttons are visible
        if (isNonTrophyAction(lastItem)) {
          this.updateFooter();
        }
        // Scroll to the top for mobile viewports
        else if (lastItem.type === "SHOW_START_BUTTONS") {
          await this.$nextTick();

          const chatContainer = document.querySelector(".jz-chat-body");
          if (chatContainer) {
            chatContainer.scrollTo(0, 0);
          }
          // do something
        }
        // Showing trophy does not display anything and requests API call
        else if (lastItem.type === "SHOW_TROPHY") {
          const finalScore: ExerciseFinalScore = {
            correctCount: this.chatProblems.results.filter(p => p.retryCount === 0).length,
            incorrectCount: this.chatProblems.results.filter(p => p.retryCount > 0).length
          };
          this.exercise = await exerciseHandler.endExercise(this, this.exercise, finalScore);

          this.nav.requireExitConfirmation = false;

          this.exercise.footer = {
            buttons: [{ text: this.$__("ex.layout.review.btn"), action: "REVIEW" }],
            disabled: false,
            show: true
          };
        }
      }
    }
  },

  async created() {
    this.micPermission = await micPermissionInspector();
    this.fetchDetails();
    await this.initChat();
    connectionCheck({
      exerciseType: EXERCISE_TYPE.CHAT,
      courseUuid: this.courseUuid,
      exerciseUuid: this.exerciseUuid
    });
    document.addEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  beforeDestroy() {
    this.audioDecoderPlayer.destroy();

    document.removeEventListener("visibilitychange", this.refreshAudioPlayer);
  },

  beforeRouteEnter(to, from, next) {
    next((vm: Vue) => {
      vm.$data.exercise = exerciseHandler.checkAssignmentId(vm.$data.exercise, from);
    });
  },

  // https://router.vuejs.org/guide/advanced/navigation-guards.html#in-component-guards
  async beforeRouteLeave(to, from, next) {
    exerciseNavHandler.beforeRouteLeave(this.nav, state => (this.nav = state), to, from, next);
  },

  methods: {
    closeConnectionMethodModal() {
      this.modalOpened = false;
    },

    exit() {
      exerciseNavHandler.exit(this, this.nav, state => (this.nav = state), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    async fetchAssignmentDetails() {
      await this.$store.dispatch(`${VUEX_ASSIGNMENT_NAMESPACE}/${DETAILS_REQUEST}`, {
        assignmentId: this.$route.params.assignmentId
      });
    },

    fetchDetails() {
      this.$store.dispatch(`${FETCH_DETAILS}`, {
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },

    onExitCancelled() {
      this.nav = exerciseNavHandler.onExitCancelled(this.nav);
    },

    onExitConfirmed() {
      exerciseNavHandler.onExitConfirm(this, this.nav, state => (this.nav = state), {
        feedbackParams: this.feedbackRouteParams
      });
    },

    async initChat() {
      // (Re-)init states
      let fallback: Location;
      if (this.assignmentId) {
        await this.fetchAssignmentDetails();
      }

      if (this.$route.params.assignmentId && this.$store.state.assignment.details) {
        fallback = {
          name: ROUTES.ASSIGNMENT.DETAILS.NAME,
          params: {
            assignmentId: this.$route.params.assignmentId
          }
        };
      } else {
        fallback = {
          name: ROUTES.COURSE.DETAILS.NAME,
          params: {
            courseUuid: this.$route.params.courseUuid
          }
        };
      }

      const newState = getNewExerciseViewState(EXERCISE_TYPE.CHAT, fallback);
      this.exercise = newState.exercise;
      this.nav = newState.nav;

      this.chat = chatHandler.getNewState();
      this.chatProblems = chatProblemsHandler.getNewState();
      this.mic = microphoneHandler.getNewState();

      // Load chat
      this.exercise.status = "loading";
      try {
        this.chat = await chatHandler.loadData(this, this.chat, this.exercise);
      } catch (err) {
        this.loadingError = this.$__("chat.error.loading");
        this.exercise.status = "error";
        return;
      }
      if (Number(this.assignmentId)) {
        this.exercise.assignmentId = Number(this.assignmentId);
      }
      this.exercise.status = "loaded";

      // Update states after data loading
      // this.header = headerHandler.loadStateFromChat(this.chat);
      this.exercise = exerciseHandler.loadHeaderFromChat(this.exercise, this.chat);
      this.chatProblems = chatProblemsHandler.loadProblemsData(this.chatProblems, this.chat);

      this.exercise = await exerciseHandler.startExercise(this, this.exercise);

      // Start the chat
      this.exercise.status = "in-progress";
      this.showNextChatMessage();
    },

    async loadProblemsQueue() {
      // Load queued messages in chat handler
      this.chat = chatHandler.loadConversationProblemsQueue(this.chat, this.chatProblems);
      this.chatProblems = chatProblemsHandler.clearQueue(this.chatProblems);
      // Show queued message
      await this.showNextChatMessage();
    },

    async onActionBtnClick(action) {
      // Start action
      if (action === "START") {
        // Immediately hide the footer buttons to avoid rapid-fire clicking. This
        // must be handled in the view, not in the handlers, for immediate reactivity
        this.exercise.footer.buttons = [];

        this.chatProblems = chatProblemsHandler.loadProblem(this.chatProblems);
        this.nav.requireExitConfirmation = true;

        await this.refreshAudioPlayer();
        this.loadProblemsQueue();
      }
      // Show review page
      else if (action === "REVIEW") {
        this.showNextChatMessage();

        this.exercise.status = "finished";
        this.chat.allowUserInteraction = true;
        this.nav.requireExitConfirmation = false;
      }
      // Motivated user wants to start again
      else if (action === "RETRY") {
        await this.initChat();
      }
      // Non-motivated user wants to get out here as soon as possible
      else if (action === "NEXT_EXERCISE") {
        if (this.limitedModeExercises && !this.nextExercise) {
          this.$router.push(TERRATALK_LIVE_ROUTES.LOBBY.PATH);
          return;
        }

        nextExerciseRouter({
          assignmentId: this.assignmentId,
          courseUuid: this.courseUuid,
          ...this.nextExercise
        });
      }
    },

    refreshAudioPlayer() {
      if (document.visibilityState === "visible") {
        console.log("refreshing audio player");
        this.audioDecoderPlayer = new AudioDecoderPlayer({ audioContext });
      }
    },

    async showNextChatMessage() {
      chatHandler.moveToNextMessage({
        state: this.chat,
        audioDecoderPlayer: await this.audioDecoderPlayer,
        onMsgDisplayStarted: async newState => {
          this.chat = newState;
          this.updateFooter();
        },
        onMsgDisplayEnded: async newState => {
          this.chat = newState;

          if (
            this.chat.status === "IntroWaitForUser" ||
            this.chat.status === "ShowTrophy" ||
            this.chat.status === "Ended"
          ) {
            // do nothing
          } else if (this.chat.status === "ProblemWaitingForUserAnswer") {
            // Update info
            this.updateFooter();
            this.chatProblems = chatProblemsHandler.onBotMessageFinished(this.chatProblems);
            this.chat.allowUserInteraction = true;

            // Problems are finished: clear remaining queue and move back to chat messages
            if (this.chatProblems.current && this.chatProblems.current.location === "end") {
              this.showNextChatMessage();
            }
          } else {
            this.showNextChatMessage();
          }
        }
      });
    },

    async onMicSend(data: MicData[]) {
      const validData = data.filter(datum => !datum.deleted && datum.recognition.value);

      // At least one valid data
      if (!validData.length) {
        return;
      }

      // Reset values
      (this.$refs.microphone as any).resetData();
      this.chat.allowUserInteraction = false;
      this.mic.disabled = true;
      this.exercise.footer.disabled = true;

      this.chat.status = "ProblemProcessing";
      this.chatProblems = await chatProblemsHandler.onUserAnswerSent(
        this,
        this.chatProblems,
        this.exercise,
        validData
      );

      this.loadProblemsQueue();
    },

    onMicTimeout() {
      MainLogger.debug("Microphone timeout detected");
      // Do something relevant
    },

    onConnectionError() {
      this.modalOpened = true;
    },

    onDictionaryClick() {
      window.alert("onDictionaryClick is TODO");
    },

    onMicStarted() {
      this.chatProblems = chatProblemsHandler.onUserAnswerStarted(this.chatProblems);
      this.chat.allowUserInteraction = false;
    },

    async onMicStopped() {
      this.chat.allowUserInteraction = true;

      const chatBody = (this.$refs as any)["chat-body"];
      if (chatBody) {
        await (chatBody as any).scrollBodyDown();
      }
    },

    onSilenceDetected() {
      this.silenceDetected = true;
    },

    updateFooter() {
      this.mic = microphoneHandler.loadStateFromChat(this.chat);
      this.exercise = exerciseHandler.loadFooterFromChat(this, this.exercise, this.chat);
    }
  }
});
